import { DOCUMENT, isPlatformBrowser } from '@angular/common';
import { Component, DestroyRef, ElementRef, PLATFORM_ID, effect, inject, signal, untracked, viewChild } from '@angular/core';

import { TranslatePipe } from '../services/translation/translate.pipe';
import { TranslationService } from '../services/translation/translation.service';
import { GuideLayout, computeGuideLayout } from './guide-layout';
import { GuideSpotlightService } from './guide-spotlight.service';
import { GuideTarget } from './setup-guide.config';
import { ElementWait, waitForElement } from './wait-for-element';

export const GUIDE_TARGET_TIMEOUT_MS = 6000;
export const GUIDE_TOAST_MS = 8000;

/**
 * Pointer walkthrough overlay, mounted once in the shell. Dims the page around a `data-guide` control, points a tapping
 * finger at it and explains it. Nothing here blocks the page: the real control stays clickable and clicking it advances.
 */
@Component({
  selector: 'app-guide-spotlight',
  imports: [TranslatePipe],
  templateUrl: './guide-spotlight.html',
  styleUrl: './guide-spotlight.scss',
})
export class GuideSpotlight {
  protected readonly guide = inject(GuideSpotlightService);
  private readonly translations = inject(TranslationService);
  private readonly document = inject(DOCUMENT);
  private readonly destroyRef = inject(DestroyRef);
  private readonly isBrowser = isPlatformBrowser(inject(PLATFORM_ID));
  private readonly tooltip = viewChild<ElementRef<HTMLElement>>('tooltip');

  protected readonly layout = signal<GuideLayout | null>(null);
  protected readonly reducedMotion = signal(false);
  private targetElement: HTMLElement | null = null;
  private wait: ElementWait | null = null;
  private frame = 0;
  private toastTimer: ReturnType<typeof setTimeout> | null = null;

  private readonly onViewportChange = () => this.scheduleMeasure();
  private readonly onDocumentClick = (event: Event) => {
    const target = this.targetElement;
    if (!target || !(event.target instanceof Node) || !target.contains(event.target)) return;
    // Let the real control handle its click first, then move on.
    setTimeout(() => this.guide.next());
  };
  private readonly onKeydown = (event: KeyboardEvent) => {
    if (event.key !== 'Escape' || !this.guide.walkthrough()) return;
    this.guide.close();
  };

  constructor() {
    if (!this.isBrowser) return;
    const view = this.document.defaultView;
    this.reducedMotion.set(view?.matchMedia?.('(prefers-reduced-motion: reduce)').matches ?? false);
    this.guide.connectRouter(this.destroyRef);

    effect(() => {
      this.guide.walkthrough();
      const target = this.guide.currentTarget();
      untracked(() => this.track(target));
    });
    effect(() => {
      const tip = this.guide.fallbackTip();
      untracked(() => {
        if (this.toastTimer) clearTimeout(this.toastTimer);
        this.toastTimer = tip ? setTimeout(() => this.guide.dismissFallback(), GUIDE_TOAST_MS) : null;
      });
    });

    view?.addEventListener('scroll', this.onViewportChange, { capture: true, passive: true });
    view?.addEventListener('resize', this.onViewportChange, { passive: true });
    this.document.addEventListener('click', this.onDocumentClick, true);
    this.document.addEventListener('keydown', this.onKeydown);
    this.destroyRef.onDestroy(() => {
      this.wait?.cancel();
      if (this.toastTimer) clearTimeout(this.toastTimer);
      if (this.frame) view?.cancelAnimationFrame(this.frame);
      view?.removeEventListener('scroll', this.onViewportChange, { capture: true });
      view?.removeEventListener('resize', this.onViewportChange);
      this.document.removeEventListener('click', this.onDocumentClick, true);
      this.document.removeEventListener('keydown', this.onKeydown);
    });
  }

  protected countLabel(index: number, total: number): string {
    return this.translations.translate('setupGuide.spotlight.count', '{current} of {total}')
      .replace('{current}', String(index + 1))
      .replace('{total}', String(total));
  }

  protected next(): void {
    const last = this.guide.isLast();
    const target = this.targetElement;
    this.guide.next();
    if (last) target?.focus?.({ preventScroll: true });
  }

  protected skip(): void {
    this.guide.close();
  }

  private track(target: GuideTarget | null): void {
    this.wait?.cancel();
    this.wait = null;
    this.targetElement = null;
    this.layout.set(null);
    if (!target) return;

    const wait = waitForElement(`[data-guide="${cssEscape(target.id)}"]`, { timeoutMs: GUIDE_TARGET_TIMEOUT_MS, root: this.document });
    this.wait = wait;
    void wait.promise.then(element => {
      if (this.wait !== wait) return;
      this.wait = null;
      if (!element) {
        this.guide.fallBack(target.tipKey);
        return;
      }
      this.targetElement = element;
      element.scrollIntoView?.({ block: 'center', inline: 'nearest', behavior: this.reducedMotion() ? 'auto' : 'smooth' });
      this.measure();
      // Measure again once the tooltip has its real height, then hand it keyboard focus.
      setTimeout(() => {
        this.measure();
        this.tooltip()?.nativeElement.focus({ preventScroll: true });
      });
    });
  }

  private scheduleMeasure(): void {
    if (!this.targetElement) return;
    const view = this.document.defaultView;
    if (!view?.requestAnimationFrame) {
      this.measure();
      return;
    }
    if (this.frame) return;
    this.frame = view.requestAnimationFrame(() => {
      this.frame = 0;
      this.measure();
    });
  }

  private measure(): void {
    const element = this.targetElement;
    if (!element) return;
    if (!element.isConnected) {
      // The page re-rendered the control: look for it again.
      this.track(this.guide.currentTarget());
      return;
    }
    const view = this.document.defaultView;
    const rect = element.getBoundingClientRect();
    const height = this.tooltip()?.nativeElement.offsetHeight || 156;
    this.layout.set(computeGuideLayout(
      { top: rect.top, left: rect.left, width: rect.width, height: rect.height },
      { width: view?.innerWidth ?? 1024, height: view?.innerHeight ?? 768 },
      height,
    ));
  }
}

function cssEscape(value: string): string {
  return typeof CSS !== 'undefined' && CSS.escape ? CSS.escape(value) : value.replace(/["\\]/g, '\\$&');
}
